import { makeAutoObservable, observable, runInAction } from "mobx";
import { type AnalyticsSource, track } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type { TIssue } from "@/types/issue";

export class IssueDetailStore {
  currentIssueId: string | null = null;
  currentProjectId: string | null = null;
  subscriptionByIssueId = observable.map<string, boolean>();

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get currentIssue(): TIssue | undefined {
    return this.currentIssueId ? this.rootStore.issue.getIssueById(this.currentIssueId) : undefined;
  }

  /**
   * Record that a user opened one work item (NG-2056): its page, the quick
   * view or a side panel. Hosts call this once the record has resolved, so a
   * failed or not-found load never counts as a view. `source` names the
   * surface it was opened from.
   */
  trackViewed(issue: TIssue, source: AnalyticsSource): void {
    track("task_viewed", {
      task_id: issue.id,
      project_id: issue.project_id ?? null,
      category: issue.category ?? null,
      source,
    });
  }

  setCurrentIssue(issueId: string | null, projectId: string | null = null): void {
    this.currentIssueId = issueId;
    this.currentProjectId = projectId;
  }

  resetCurrentIssue(): void {
    this.currentIssueId = null;
    this.currentProjectId = null;
  }

  /** Seed the subscription toggle from a full record the cache read. */
  seedSubscription(issue: TIssue): void {
    this.subscriptionByIssueId.set(issue.id, Boolean(issue.is_subscribed));
  }

  async refreshCurrentIssue(workspaceSlug: string): Promise<void> {
    if (!this.currentIssueId || !this.currentProjectId) return;
    // The cache's record of it, read again, and every list row of it with it.
    const issue = await this.rootStore.issue.fetchIssueByIdentifier(
      workspaceSlug,
      this.currentIssueId,
      { fresh: true },
    );
    this.seedSubscription(issue);
  }

  async toggleSubscription(workspaceSlug: string): Promise<void> {
    if (!this.currentIssueId || !this.currentProjectId) return;
    await this.toggleSubscriptionForIssue(
      workspaceSlug,
      this.currentProjectId,
      this.currentIssueId,
      "page",
    );
  }

  async toggleSubscriptionForIssue(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const previous = this.subscriptionByIssueId.get(issueId) ?? false;

    this.subscriptionByIssueId.set(issueId, !previous);

    try {
      if (previous) {
        await this.rootStore.workItemService.unsubscribe(workspaceSlug, projectId, issueId);
      } else {
        await this.rootStore.workItemService.subscribe(workspaceSlug, projectId, issueId);
      }
      track(previous ? "task_unsubscribed" : "task_subscribed", {
        task_id: issueId,
        project_id: projectId,
        source,
      });
    } catch (error) {
      runInAction(() => {
        this.subscriptionByIssueId.set(issueId, previous);
      });
      throw error;
    }
  }
}
